import { FunctionsHttpError } from "@supabase/supabase-js";
import { supabase } from "./supabase";

// Abrir uma porta que precisa de credencial gerada na hora (Fase 4, 000062).
//
// Duas das quatro portas de agora não têm endereço fixo: Aulas precisa de um
// link mágico do Memberkit da escola daquele curso, e o CFC de A a Z precisa de
// uma sessão criada pelo SSO deles. Quem gera é a Edge Function `abrir-card`,
// porque é lá que os segredos moram — e é o banco, dentro dela, que decide se o
// aluno pode.
//
// As portas de endereço fixo (Comunidade, Contabiliza.AI) NÃO passam por aqui: a
// `vw_minha_vitrine` entrega a url e o card é um link comum. Menos peça no
// caminho, menos coisa para falhar.

/** Por que não abriu. Vem do banco ou da origem; a tela traduz cada um. */
export type MotivoAbertura =
  // o banco recusou
  | "sem-acesso"
  | "trancado"
  | "sem-perfil"
  // estado que impede, e não há nada a consertar no ar
  | "em-breve"
  | "nao-e-membro"
  | "sem-escola"
  | "destino-desconhecido"
  | "destino-nao-implementado"
  // os dois dos cursos bônus (000177)
  | "aula-desconhecida"
  | "curso-bonus-nao-cadastrado"
  // problema nosso ou da origem
  | "sem-chave"
  | "falha"
  | "endereco-recusado"
  | "sem-sessao"
  // o «ver como» do master (000335): a tela não chama a function, por desenho
  | "ver-como";

export type Abertura =
  | { ok: true; interna?: undefined; url: string }
  | { ok: true; interna: true }
  | { ok: false; motivo: MotivoAbertura };

const MOTIVOS: MotivoAbertura[] = [
  "sem-acesso",
  "trancado",
  "sem-perfil",
  "em-breve",
  "nao-e-membro",
  "sem-escola",
  "destino-desconhecido",
  "destino-nao-implementado",
  "aula-desconhecida",
  "curso-bonus-nao-cadastrado",
  "sem-chave",
  "falha",
  "endereco-recusado",
  "sem-sessao",
  "ver-como",
];

function comoMotivo(valor: unknown): MotivoAbertura {
  return MOTIVOS.includes(valor as MotivoAbertura) ? (valor as MotivoAbertura) : "falha";
}

/**
 * Pede o endereço de destino. Nunca lança.
 *
 * `curso` é o slug do curso em cuja tela o aluno clicou — é ele que resolve qual
 * escola do Memberkit atende. `null` para card de escopo 'conta' (Contrate um
 * Contador), que não pertence a curso nenhum.
 *
 * `aula` é o id da aula no Memberkit, e só existe para os cursos bônus (000177):
 * com ele, o destino é a AULA dentro da escola em vez da raiz dela. Não é uma
 * segunda function nem um segundo caminho de autorização — o banco responde por
 * `bonus_abrir_aula`, que pergunta a `abrir_card` por dentro.
 *
 * O id vai como STRING de propósito: os ids do Memberkit já têm 7 dígitos, e
 * `JSON.stringify` de um number perde precisão acima de 2^53. A Edge Function
 * aceita as duas formas; mandar string é a que continua correta quando eles
 * crescerem.
 */
export async function abrirCard(
  card: string,
  curso: string | null,
  aula?: number | string | null,
): Promise<Abertura> {
  try {
    const { data: sessao } = await supabase.auth.getSession();
    const token = sessao.session?.access_token;
    if (!token) return { ok: false, motivo: "sem-sessao" };

    const { data, error } = await supabase.functions.invoke("abrir-card", {
      body: {
        card,
        curso,
        ...(aula === undefined || aula === null ? {} : { aula: String(aula) }),
      },
      headers: { Authorization: `Bearer ${token}` },
    });

    if (error) {
      // O motivo útil está no CORPO da resposta, não em `error.message` — em
      // status 4xx/5xx o supabase-js devolve `data: null` e guarda a resposta
      // inteira em `error.context`. Sem ler daí, "assine o contrato para
      // liberar" e "o Memberkit não respondeu" viram a mesma tela genérica.
      if (error instanceof FunctionsHttpError) {
        try {
          const corpo = await error.context.json();
          return { ok: false, motivo: comoMotivo(corpo?.motivo) };
        } catch {
          return { ok: false, motivo: "falha" };
        }
      }
      return { ok: false, motivo: "falha" };
    }

    // Ferramenta que MORA no hub (000074/000076): não há credencial a gerar, e
    // a function responde só "é interna" — quem deriva a rota é o front
    // (`rotaDaFerramenta`), porque a derivação tem um dono só (000065). Este
    // caminho é o do link antigo/digitado: o clique normal nem passa por aqui.
    if ((data as { interno?: unknown } | null)?.interno === true) {
      return { ok: true, interna: true };
    }

    const url = (data as { url?: unknown } | null)?.url;
    if (typeof url !== "string" || url === "") return { ok: false, motivo: "falha" };
    return { ok: true, url };
  } catch {
    return { ok: false, motivo: "falha" };
  }
}

/**
 * O que o aluno lê quando não abre.
 *
 * Cada motivo ganha texto próprio porque as ações são diferentes: "assine o
 * contrato" é coisa que ele resolve, "ainda estamos liberando" é esperar, e
 * "falhou" é tentar de novo. Uma mensagem genérica para os três manda todo mundo
 * ao suporte, inclusive quem tinha a solução na mão.
 */
export function mensagemDaAbertura(motivo: MotivoAbertura): { titulo: string; texto: string } {
  switch (motivo) {
    case "trancado":
      return {
        titulo: "Falta assinar o contrato",
        texto:
          "As ferramentas e as aulas deste curso abrem assim que o contrato estiver assinado. O caminho está na página do curso.",
      };
    case "sem-acesso":
      return {
        titulo: "Isto não está na sua lista",
        texto:
          "Este item não pertence a nenhum curso com acesso ativo na sua conta. Se você comprou e ele não aparece, fale com o suporte que a gente confere.",
      };
    case "sem-perfil":
      return {
        titulo: "Não encontramos o seu cadastro",
        texto: "Nenhuma compra está ligada a este e-mail. Fale com o suporte para conferir.",
      };
    case "em-breve":
      return {
        titulo: "Em breve",
        texto: "Esta porta ainda não está aberta por aqui. Estamos trazendo uma por uma.",
      };
    case "nao-e-membro":
      return {
        titulo: "Suas aulas estão sendo liberadas",
        texto:
          "O seu acesso está registrado aqui, mas a plataforma de aulas ainda não recebeu o seu cadastro. Isso costuma levar alguns minutos — se passar disso, fale com o suporte.",
      };
    case "ver-como":
      // Quem lê isto é o MASTER, vendo o hub como outra pessoa: o link seria
      // gerado na hora para uma conta de verdade, e o «ver como» não entra em
      // sistema de fora (000335). Não é defeito, e a frase diz o caminho.
      return {
        titulo: "Não abre no «ver como»",
        texto:
          "Este destino gera um acesso na hora, e ele entraria numa conta de verdade. Volte ao normal (na barra do pé da tela) para abrir com a sua conta.",
      };
    case "sem-sessao":
      return {
        titulo: "Sua sessão expirou",
        texto: "Entre de novo para continuar.",
      };
    case "aula-desconhecida":
      // O catálogo do hub é sincronizado do Memberkit, e aula recriada lá ganha
      // id novo — foi assim que 3 dos 25 links do export do Bubble morreram. Ou
      // seja: este motivo é quase sempre "a lista aqui está velha", e a ação do
      // aluno é recarregar. Dizer "não existe" faria ele achar que a aula sumiu.
      return {
        titulo: "Esta aula mudou de lugar",
        texto:
          "A lista de aulas é sincronizada com a plataforma de vídeo, e esta não está mais na versão que você tem aberta. Atualize a página para ver a lista de agora.",
      };
    case "curso-bonus-nao-cadastrado":
      return {
        titulo: "Estamos preparando este curso",
        texto:
          "O acesso está liberado na sua conta e as aulas ainda não foram cadastradas aqui. Se demorar, fale com o suporte que a gente resolve.",
      };
    default:
      // sem-escola, sem-chave, destino-*, falha, endereco-recusado. Todos são
      // problema nosso, e nenhum deles é acionável pelo aluno — ele não tem o que
      // fazer com "a variável de ambiente não está no deploy". A tentativa fica
      // registrada em `aberturas_de_card`, que é verdade e não promessa.
      return {
        titulo: "Não conseguimos abrir agora",
        texto:
          "O problema é nosso, não seu: a tentativa fica registrada para a equipe. Tente de novo em alguns minutos.",
      };
  }
}
